import React, { useCallback } from "react";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuList from "@mui/material/MenuList";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { map } from "ramda";
import MenuItem from "@mui/material/MenuItem";
import { notBlank } from "../util/ext";

const ListButton = ({
  title,
  onSelect,
  variant = "icon",
  options = [],
  icon,
}) => {
  const anchorRef = React.useRef(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(menuOpen);
  React.useEffect(() => {
    if (prevOpen.current === true && menuOpen === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = menuOpen;
  }, [menuOpen]);

  const handleListKeyDown = useCallback(event => {
    if (event.key === "Tab") {
      event.preventDefault();
      setMenuOpen(false);
    }
  }, []);

  const handleClicked = useCallback(() => {
    setMenuOpen(prevOpen => !prevOpen);
  }, []);

  const handleMenuClose = useCallback(event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setMenuOpen(false);
  }, []);

  const handleMenuSelect = useCallback(
    value => {
      if (notBlank(onSelect)) {
        onSelect(value);
      }
      setMenuOpen(false);
    },
    [onSelect],
  );

  return (
    <Box>
      {variant === "button" ? (
        <Button
          ref={anchorRef}
          variant={"text"}
          startIcon={icon}
          aria-controls={menuOpen ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleClicked}
        >
          {title}
        </Button>
      ) : (
        <Tooltip title={title}>
          <IconButton
            ref={anchorRef}
            aria-controls={menuOpen ? "menu-list-grow" : undefined}
            aria-haspopup="true"
            onClick={handleClicked}
            size="large"
          >
            {icon}
          </IconButton>
        </Tooltip>
      )}
      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom"
        style={{ zIndex: 100 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper variant={"outlined"}>
              <ClickAwayListener onClickAway={handleMenuClose}>
                <MenuList
                  autoFocusItem={menuOpen}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {map(
                    opt => (
                      <MenuItem
                        key={opt.value}
                        onClick={() => handleMenuSelect(opt.value)}
                      >
                        {opt.display}
                      </MenuItem>
                    ),
                    options,
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

ListButton.propTypes = {
  title: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  variant: PropTypes.oneOf(["button", "icon"]),
  options: PropTypes.array,
  icon: PropTypes.node.isRequired,
};

export default ListButton;
